import { usePathname } from 'next/navigation';
import Script from 'next/script';

import { useComplianceLevelState } from '@/hooks/store';
import { NO_RESTRICTIONS, REQUIRE_GDPR_MODAL } from '@/store/complianceLevel/types';

export interface LayoutScriptsProps {
	/** Per-request CSP nonce (see proxy.ts) applied to every genuinely inline <Script> below. */
	nonce?: string | null;
}

const LayoutScripts = ({ nonce }: LayoutScriptsProps) => {
	const complianceLevel = useComplianceLevelState();
	const pathname = usePathname();
	// once we move over to Auth0, we may be able to remove this code.
	// this is here because it is impossible to prevent sensitive path params, like reset tokens,
	// ...from leaking to these analytics providers.
	const isPasswordResetPage = pathname?.includes('/password-reset/');

	return (
		<>
			<Script id="browser-warning-init" nonce={nonce ?? undefined}>
				{`
          var isIE11 = !!window.MSInputMethodContext && !!document.documentMode;
          // true on IE11
          // false on Edge and other IEs/browsers.
          if (isIE11) {
            window.location = '/browserwarning.html?browser=ie11';
          }
        `}
			</Script>
			{/* Non-production tiers send the same events a real visitor does, so their traffic
			    would be indistinguishable once the container is loaded. */}
			{process.env.NEXT_PUBLIC_CALM_ENV === 'production' &&
				complianceLevel === NO_RESTRICTIONS &&
				!isPasswordResetPage && (
					<>
						<Script id="gtm-init" nonce={nonce ?? undefined}>
							{`
          window.dataLayer = window.dataLayer || [];
		  function gtag() { dataLayer.push(arguments); }
		  gtag('consent', 'default', {
			'ad_user_data': 'granted',
			'ad_personalization': 'granted',
			'ad_storage': 'granted',
			'analytics_storage': 'granted'
		  });
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${process.env.NEXT_PUBLIC_GTM_CONTAINER}');
        `}
						</Script>
						<noscript>
							<iframe
								title="gtm-body"
								src={`https://www.googletagmanager.com/ns.html?id=${process.env.NEXT_PUBLIC_GTM_CONTAINER}`}
								height="0"
								width="0"
								style={{ display: 'none', visibility: 'hidden' }}
							/>
						</noscript>
					</>
				)}
			{process.env.NEXT_PUBLIC_CALM_ENV === 'production' && complianceLevel !== REQUIRE_GDPR_MODAL && (
				<Script id="px-init" nonce={nonce ?? undefined} strategy="afterInteractive">
					{`
            (function(){
              window._pxAppId = '${process.env.NEXT_PUBLIC_PX_APP_ID}';
              var p = document.getElementsByTagName('script')[0],
                  s = document.createElement('script');
              s.async = 1;
              // PerimeterX's init script path uses the app ID without its leading "PX" (e.g. PX12Xk43jk -> 12Xk43jk).
              s.src = '${process.env.NEXT_PUBLIC_API_ENDPOINT}/${process.env.NEXT_PUBLIC_PX_APP_ID?.replace(
								/^PX/,
								'',
							)}/init.js';
              p.parentNode.insertBefore(s,p);
            }());
          `}
				</Script>
			)}
			{complianceLevel === NO_RESTRICTIONS && (
				<Script id="schema-init" type="application/ld+json">
					{`
            {
              "@context": "http://schema.org",
              "@type": "Organization",
              "url": "https://www.calm.com",
              "logo": "https://www.calm.com/_n/images/calm-logo-gradient.png"
            }
          `}
				</Script>
			)}
		</>
	);
};

export default LayoutScripts;
